/* eslint-disable no-console */
import React, { useMemo } from "react";
import { ToastContainer } from "@jsq/ds/core";
import ArenaProvider from "common/containers/Common/Arena/Provider";
import { FeatureFlagProvider } from "@jsq/flags-react";
import { UserProvider } from "apps/staff/Staff/common/UserProvider";
import {
  ApplicationContext,
  ApplicationEnvironment,
} from "common/constants/ApplicationContext";
import withDataProvider from "common/hocs/withDataProvider";
import { Pillar } from "./Pillar";

/**
 * A provider to wrap Arena and User providers to make it easier to skip
 * them for outlook subdomain.
 */
const StaffProvidersWrapper = ({ children }: { children?: React.ReactNode }) => (
  <ArenaProvider>
    <UserProvider>{children}</UserProvider>
  </ArenaProvider>
);

/** Common providers that should set above all pillars */
const Providers = ({ children }: { children?: React.ReactNode }) => {
  const OutlookConditionalProviders = useMemo(() => {
    const firstSubdomain = window.location.hostname.split(".").at(0);
    // When on outlook subdomain, skip both Arena and User providers
    // since users are not initially logged in on outlook, and it's not a valid arena
    return firstSubdomain === "outlook" ? React.Fragment : StaffProvidersWrapper;
  }, []);

  return (
    <FeatureFlagProvider applicationName="staff">
      <OutlookConditionalProviders>
        <ApplicationContext.Provider value={ApplicationEnvironment.Staff}>
          {children}
        </ApplicationContext.Provider>
      </OutlookConditionalProviders>
    </FeatureFlagProvider>
  );
};

const App = () => {
  const pillarsToLoad = document.querySelectorAll("[data-pillar='jsqPillar']");

  if (pillarsToLoad.length === 0) {
    return null;
  }

  // find and render all pillars into their respective nodes
  const pillarComponents = Array.from(pillarsToLoad).map((pillarElement) => {
    const pillar = pillarElement as HTMLElement;
    const pillarName = pillar.dataset.name;
    const pillarProps = pillar.dataset.props;
    const node = document.getElementById(pillar.id);

    if (!pillarName || !pillarProps || !node) {
      console.error(`Pillar information missing ${pillarName}`);
      return null;
    }

    try {
      const props = JSON.parse(pillarProps) as Record<string, string>;

      return (
        <Pillar
          key={`${pillarName}-${pillar.id}`}
          name={pillarName}
          node={node}
          props={props}
        />
      );
    } catch (e) {
      console.error(`Unable to render pillar ${pillarName}`, e);
      return null;
    }
  });

  return (
    <Providers>
      <ToastContainer />
      {pillarComponents}
    </Providers>
  );
};

const PillarsAppWithDataProvider = withDataProvider(App);

export { PillarsAppWithDataProvider as App };
